import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CommentOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  LineChartOutlined,
  NotificationOutlined,
  ReloadOutlined,
  RightOutlined,
  SafetyCertificateOutlined,
  StopOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Button, Empty, List, Space, Spin, Tag, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { ProjectAlertKind, ProjectAlertSeverity } from '../../api/projects';
import type {
  ProjectAlert,
  ProjectAlertSeverityValue,
  ProjectAlertSummary,
} from '../../api/projects';
import { AppRoutes } from '../../routes';
import { BrandColors } from '../../theme/brand';

/*
  Khai theo đúng tập giá trị hợp lệ chứ không theo kiểu số chung. Khai theo số chung thì phép tra
  bảng trả về một giá trị có thể rỗng, và mọi nơi dùng phải xử lý một trường hợp không xảy ra được.
  Khai như dưới đây thì bộ kiểm kiểu cũng bắt lỗi ngay khi máy chủ thêm một mức mà quên khai ở đây.
*/
/** Màu của thẻ mức độ, theo đúng bảng màu chuẩn của thư viện thành phần. */
const severityColors: Record<ProjectAlertSeverityValue, string> = {
  [ProjectAlertSeverity.Critical]: 'error',
  [ProjectAlertSeverity.Warning]: 'warning',
  [ProjectAlertSeverity.Information]: 'processing',
};

/** Chữ trên thẻ mức độ. */
const severityLabels: Record<ProjectAlertSeverityValue, string> = {
  [ProjectAlertSeverity.Critical]: 'Cần xử lý ngay',
  [ProjectAlertSeverity.Warning]: 'Cần chú ý',
  [ProjectAlertSeverity.Information]: 'Để biết',
};

/**
 * Biểu tượng của từng loại cảnh báo.
 *
 * Biểu tượng nói loại việc, còn màu nói mức độ. Tách hai thứ ra để người đọc lướt qua là phân được
 * <i>việc của tôi</i> với <i>việc của đội</i> mà không phải đọc hết câu chữ.
 */
function alertIcon(kind: number, color: string): ReactNode {
  const style = { color, fontSize: 18 };

  switch (kind) {
    case ProjectAlertKind.MyTaskOverdue:
    case ProjectAlertKind.ProjectOverdue:
      return <ClockCircleOutlined style={style} />;
    case ProjectAlertKind.MyTaskNotAccepted:
      return <ExclamationCircleOutlined style={style} />;
    case ProjectAlertKind.MyTaskBlocked:
    case ProjectAlertKind.TeamTaskBlocked:
      return <StopOutlined style={style} />;
    case ProjectAlertKind.ProjectUnhealthy:
      return <WarningOutlined style={style} />;
    case ProjectAlertKind.SuspiciousProgress:
      return <LineChartOutlined style={style} />;
    case ProjectAlertKind.MyTaskHasNewComment:
      return <CommentOutlined style={style} />;
    case ProjectAlertKind.MentionedInComment:
      return <NotificationOutlined style={style} />;
    case ProjectAlertKind.WarrantyExpiringSoon:
      return <SafetyCertificateOutlined style={style} />;
    default:
      return <InfoCircleOutlined style={style} />;
  }
}

/** Màu của biểu tượng, theo mức độ. */
function iconColor(severity: number): string {
  switch (severity) {
    case ProjectAlertSeverity.Critical:
      return BrandColors.red;
    case ProjectAlertSeverity.Warning:
      return BrandColors.goldDeep;
    default:
      return BrandColors.blue;
  }
}

/**
 * Màn hình mà một dòng cảnh báo dẫn tới.
 *
 * Cảnh báo mà bấm vào không đi đâu thì chỉ là một lời than. Mỗi dòng ở đây đưa thẳng tới nơi xử lý
 * được việc đó: cảnh báo về việc của tôi thì tới danh sách việc của tôi, cảnh báo về một dự án cụ
 * thể thì tới chính dự án đó.
 */
function alertTarget(alert: ProjectAlert): string {
  switch (alert.kind) {
    case ProjectAlertKind.MyTaskOverdue:
    case ProjectAlertKind.MyTaskNotAccepted:
    case ProjectAlertKind.MyTaskBlocked:
      return AppRoutes.MyTasks;
    case ProjectAlertKind.TeamTaskBlocked:
      return AppRoutes.ProjectBoard;
    case ProjectAlertKind.MyTaskHasNewComment:
      /*
        Bình luận nằm trên công việc được giao cho tôi, nên danh sách việc của tôi là nơi gần nhất
        tới chỗ đọc chúng.
      */
      return AppRoutes.MyTasks;
    case ProjectAlertKind.MentionedInComment:
      /*
        Bị gọi tên có thể xảy ra ở việc KHÔNG giao cho tôi, nên không đưa về danh sách việc của tôi
        — đó chính là trường hợp nó sẽ không có ở đó. Đưa về danh sách dự án.
      */
      return AppRoutes.Project;
    case ProjectAlertKind.SuspiciousProgress:
      /*
        Con số đáng ngờ nằm rải ở nhiều dự án nên không có một màn hình chung. Đưa về danh sách dự
        án: từ đó mở cây công việc của đúng dự án rồi bấm "Con số đáng ngờ" là tới nơi.
      */
      return AppRoutes.Project;
    default:
      return alert.projectId === null
        ? AppRoutes.Project
        : `${AppRoutes.Project}/${alert.projectId}`;
  }
}

/**
 * Danh sách những việc cần chú ý.
 *
 * <b>Gộp theo loại kèm số lượng, không liệt kê từng bản ghi.</b> Một người quản lý ba dự án có thể
 * có hai mươi việc quá hạn; hai mươi dòng riêng lẻ sẽ lấp kín màn hình rồi bị bỏ qua hết. Một dòng
 * <i>bạn có 20 việc quá hạn</i> kèm ba ví dụ thì đọc được, và bấm vào là tới chỗ xem đủ.
 *
 * <b>Khi không có gì thì nói rõ là không có gì.</b> Một vùng trống ở chỗ đáng ra có cảnh báo làm
 * người dùng phân vân không biết hệ thống đã tính xong chưa.
 */
export function ProjectAlertList({
  summary,
  isLoading,
  onReload,
}: {
  readonly summary: ProjectAlertSummary | null;
  readonly isLoading: boolean;
  readonly onReload: () => void;
}): ReactNode {
  const navigate = useNavigate();

  if (summary === null) {
    return isLoading ? <Spin /> : null;
  }

  if (summary.alerts.length === 0) {
    return (
      <Empty
        image={<CheckCircleOutlined style={{ fontSize: 40, color: BrandColors.green }} />}
        description="Không có việc nào cần chú ý. Không có việc quá hạn, không có việc bị chặn."
      />
    );
  }

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <List<ProjectAlert>
        size="small"
        dataSource={summary.alerts as ProjectAlert[]}
        rowKey={(alert) => alert.kind.toString()}
        renderItem={(alert) => (
          <List.Item
            actions={[
              <Button
                key="open"
                size="small"
                type="link"
                icon={<RightOutlined />}
                onClick={() => {
                  void navigate(alertTarget(alert));
                }}
              >
                Xem
              </Button>,
            ]}
          >
            <List.Item.Meta
              avatar={alertIcon(alert.kind, iconColor(alert.severity))}
              title={
                <Space size={8} wrap>
                  <span>{alert.message}</span>
                  <Tag color={severityColors[alert.severity]}>
                    {severityLabels[alert.severity]}
                  </Tag>
                </Space>
              }
              description={
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {alert.detail}
                </Typography.Text>
              }
            />
          </List.Item>
        )}
      />

      <Space style={{ justifyContent: 'space-between', width: '100%' }} wrap>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Cảnh báo được tính lại từ dữ liệu thật ở mỗi lần mở, nên không có nút đánh dấu đã đọc:
          dòng nào cũng tự biến mất khi việc đằng sau nó được xử lý.
        </Typography.Text>
        <Button size="small" icon={<ReloadOutlined />} onClick={onReload}>
          Tải lại
        </Button>
      </Space>
    </Space>
  );
}
